import { useEffect, useMemo, useRef } from 'react'

import type { AwardPortfolioItem, TimeSeriesPoint } from '@/types/usaspending'
import type { IdvGroupTimeSeriesResponse } from '@/types/usaspending/analytics'

import {
  type ChartMode,
  type CumulativePoint,
  buildCategoryBreakdownCurves,
  computeMonthlyObligation,
  computeMonthlyOutlay,
  computeRealMonthlyOutlay,
  decumulate,
} from '../../charts/cashflow-utils'
import {
  buildAggregateOption,
  buildCategoryBreakdownOption,
  buildIdvGroupOption,
  buildSingleAwardOption,
} from '../../charts/echarts-cashflow-options'
import type { EChartsOption } from '../../charts/echarts-setup'
import type {
  CashFlowSelection,
  GroupedPortfolio,
  IdvGroup,
} from '../../charts/idv-grouping'
import {
  type TimeRange,
  getTimeRangeWindow,
} from '../../shared/TimeRangeToggle'
import type { DrillLevel } from './useDrillDown'

interface UseExpandedChartOptionsInput {
  effectiveMode: ChartMode
  drillLevel: DrillLevel
  selection: CashFlowSelection
  /** Whether the chart is in expanded (explorer) mode — controls grid padding, axisPointer, dataZoom */
  expanded: boolean

  // Aggregate curves
  timeSeries: TimeSeriesPoint[]
  obligationCurve: CumulativePoint[]
  ceilingCurve: CumulativePoint[] | null
  ceilingPreRange: number
  outlayTimeSeries: TimeSeriesPoint[] | undefined
  outlayRatio: number
  preReportingOutlay: number | null
  outlayCurve: CumulativePoint[] | null
  hasPreReportingData: boolean
  estimatedOutlayPct: number
  fiscalYear: number | 'all'
  timeRange?: TimeRange

  // Grouped portfolio (for category breakdown)
  grouped: GroupedPortfolio

  // Per-selection data
  selectedAward: AwardPortfolioItem | null
  awardTimeSeries: TimeSeriesPoint[] | undefined
  isAwardTimeSeriesLoading: boolean
  selectedIdvGroup: IdvGroup | null
  idvGroupTimeSeriesData: IdvGroupTimeSeriesResponse | undefined
  isIdvGroupTimeSeriesLoading: boolean
  singleChildTimeSeries: TimeSeriesPoint[] | undefined

  isChartLoading: boolean
}

export function useExpandedChartOptions(input: UseExpandedChartOptionsInput) {
  const {
    effectiveMode,
    drillLevel,
    selection,
    expanded,
    timeSeries,
    obligationCurve,
    ceilingCurve,
    ceilingPreRange,
    outlayTimeSeries,
    outlayRatio,
    preReportingOutlay,
    outlayCurve,
    hasPreReportingData,
    estimatedOutlayPct,
    fiscalYear,
    timeRange,
    grouped,
    selectedAward,
    awardTimeSeries,
    isAwardTimeSeriesLoading,
    selectedIdvGroup,
    idvGroupTimeSeriesData,
    isIdvGroupTimeSeriesLoading,
    singleChildTimeSeries,
    isChartLoading,
  } = input

  // Monthly curves for aggregate monthly mode
  const monthlyObligation = useMemo(
    () => computeMonthlyObligation(timeSeries),
    [timeSeries],
  )

  const monthlyCeiling = useMemo(
    () =>
      effectiveMode === 'monthly' && ceilingCurve
        ? decumulate(ceilingCurve, ceilingPreRange)
        : null,
    [ceilingCurve, effectiveMode, ceilingPreRange],
  )

  const monthlyOutlay = useMemo(() => {
    if (effectiveMode !== 'monthly' || !monthlyObligation) return null
    if (outlayTimeSeries && outlayTimeSeries.length > 0) {
      return computeRealMonthlyOutlay(
        outlayTimeSeries,
        monthlyObligation,
        outlayRatio,
        preReportingOutlay,
      )
    }
    return computeMonthlyOutlay(monthlyObligation, outlayRatio)
  }, [
    monthlyObligation,
    outlayRatio,
    effectiveMode,
    outlayTimeSeries,
    preReportingOutlay,
  ])

  // Category breakdown curves (for Level 1)
  const categoryBreakdown = useMemo(() => {
    if (drillLevel.level !== 1) return null
    return buildCategoryBreakdownCurves(grouped, timeSeries)
  }, [drillLevel, grouped, timeSeries])

  // Per-level chart options — each only recomputes when its own inputs change.

  // Level 0: Aggregate option
  const aggregateOption = useMemo(() => {
    const displayObligation =
      effectiveMode === 'monthly' && monthlyObligation
        ? monthlyObligation
        : obligationCurve
    const displayCeiling =
      effectiveMode === 'monthly' ? monthlyCeiling : ceilingCurve
    const displayOutlay =
      effectiveMode === 'monthly' ? monthlyOutlay : outlayCurve
    return buildAggregateOption({
      obligationCurve: displayObligation,
      ceilingCurve: displayCeiling,
      outlayCurve: displayOutlay,
      hasPreReportingData,
      expanded,
      chartMode: effectiveMode,
      estimatedOutlayPct,
      fiscalYear,
    })
  }, [
    effectiveMode,
    obligationCurve,
    monthlyObligation,
    monthlyCeiling,
    monthlyOutlay,
    ceilingCurve,
    outlayCurve,
    hasPreReportingData,
    estimatedOutlayPct,
    fiscalYear,
    expanded,
  ])

  // Level 1: Category breakdown option
  const categoryOption = useMemo(() => {
    if (!categoryBreakdown) return null
    const isMonthly = effectiveMode === 'monthly'
    return buildCategoryBreakdownOption({
      contractCurve: isMonthly
        ? decumulate(categoryBreakdown.contractCurve)
        : categoryBreakdown.contractCurve,
      grantCurve: isMonthly
        ? decumulate(categoryBreakdown.grantCurve)
        : categoryBreakdown.grantCurve,
      idvCurve: isMonthly
        ? decumulate(categoryBreakdown.idvCurve)
        : categoryBreakdown.idvCurve,
      expanded,
      chartMode: effectiveMode,
    })
  }, [categoryBreakdown, effectiveMode, expanded])

  // Level 2: Single award option
  const singleAwardOption = useMemo(() => {
    if (!selectedAward || isAwardTimeSeriesLoading) return null
    if (!awardTimeSeries || awardTimeSeries.length === 0) return null
    return buildSingleAwardOption({
      award: selectedAward,
      timeSeries: awardTimeSeries,
      chartMode: effectiveMode,
      expanded,
    })
  }, [
    selectedAward,
    awardTimeSeries,
    isAwardTimeSeriesLoading,
    effectiveMode,
    expanded,
  ])

  // Level 2: IDV group option
  const idvGroupOption = useMemo(() => {
    if (!selectedIdvGroup || isIdvGroupTimeSeriesLoading) return null
    if (effectiveMode === 'monthly' && singleChildTimeSeries?.length) {
      return buildSingleAwardOption({
        award: selectedIdvGroup.children[0],
        timeSeries: singleChildTimeSeries,
        chartMode: 'monthly',
        expanded,
      })
    }
    return buildIdvGroupOption({
      group: selectedIdvGroup,
      obligationCurve,
      chartMode: effectiveMode,
      idvGroupTimeSeries: idvGroupTimeSeriesData ?? null,
      expanded,
    })
  }, [
    selectedIdvGroup,
    isIdvGroupTimeSeriesLoading,
    effectiveMode,
    singleChildTimeSeries,
    obligationCurve,
    idvGroupTimeSeriesData,
    expanded,
  ])

  // Select option by drill level + derive noTimeSeriesData flag
  const { chartOption, noTimeSeriesData } = useMemo(() => {
    if (drillLevel.level === 1 && categoryOption)
      return { chartOption: categoryOption, noTimeSeriesData: false }
    if (
      selection?.type === 'award' &&
      selectedAward &&
      !isAwardTimeSeriesLoading
    ) {
      if (singleAwardOption)
        return { chartOption: singleAwardOption, noTimeSeriesData: false }
      return { chartOption: aggregateOption, noTimeSeriesData: true }
    }
    if (
      selection?.type === 'idv' &&
      selectedIdvGroup &&
      !isIdvGroupTimeSeriesLoading
    ) {
      if (idvGroupOption)
        return { chartOption: idvGroupOption, noTimeSeriesData: false }
    }
    return { chartOption: aggregateOption, noTimeSeriesData: false }
  }, [
    drillLevel,
    selection,
    selectedAward,
    isAwardTimeSeriesLoading,
    selectedIdvGroup,
    isIdvGroupTimeSeriesLoading,
    aggregateOption,
    categoryOption,
    singleAwardOption,
    idvGroupOption,
  ])

  // Compute the zoom window for dispatchAction (animated zoom/pan).
  // Kept separate from the option so setOption never triggers a series re-render
  // when only the time range changes.
  const zoomWindow = useMemo(
    () => (timeRange ? getTimeRangeWindow(timeRange) : null),
    [timeRange],
  )

  // Freeze the displayed option while loading — chart stays exactly as it was
  // before the click, preventing a flash back to the aggregate view.
  const prevChartOptionRef = useRef<EChartsOption>(chartOption)
  useEffect(() => {
    if (!isChartLoading) {
      prevChartOptionRef.current = chartOption
    }
  }, [isChartLoading, chartOption])
  const displayChartOption = isChartLoading
    ? prevChartOptionRef.current
    : chartOption

  return { displayChartOption, zoomWindow, noTimeSeriesData }
}
